import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logOut } from '../../firebase/authService';
import { useAuth } from '../../contexts/AuthContext';
import { BADGE_CONFIG, computeBadge } from '../../utils/driverBadge';
import { ReactComponent as TippiLogoLight } from '../../assets/logo-light.svg';
import { COLORS, FONTS, SHADOWS } from '../../styles/tokens';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useOrgMembership } from '../../hooks/useOrgMembership';
import { useCarrierOrg } from '../../hooks/useCarrierOrg';
import { useTheme } from '../../contexts/ThemeContext';
import { isOrdererRootPath } from '../orderer/OrdererTabBar';

const ROLE_LABEL = { driver: 'Sjåfør', orderer: 'Bestiller', admin: 'Admin', carrier: 'Transportør' };
const ROLE_COLOR = { driver: COLORS.success, orderer: COLORS.blue, admin: COLORS.purple, carrier: COLORS.warning };

function getInitials(email, displayName) {
  if (displayName) return displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (email ?? '?')[0].toUpperCase();
}

function useNarrow() {
  const [narrow, setNarrow] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return narrow;
}

export default function Navbar() {
  const { currentUser, userRoles, activeRole, setActiveRole, effectiveRole, viewingAs, setViewingAs, userDoc } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef   = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current   && !btnRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  async function handleLogout() {
    setOpen(false);
    await logOut();
    navigate('/login');
  }

  function navTo(path) {
    setOpen(false);
    navigate(path);
  }

  const { unreadCount } = useUnreadCount();
  const { orgIsAdmin } = useOrgMembership();
  const { isAdmin: carrierIsAdmin } = useCarrierOrg();
  const { pathname } = useLocation();
  const narrow = useNarrow();
  const initials  = getInitials(currentUser?.email, userDoc?.name);
  const roleColor = ROLE_COLOR[effectiveRole] ?? '#888';

  // Bestiller på desktop: hele menyen ligger i toppen i stedet for bak profilikonet
  const ordererLinks = [
    { label: 'Oppdrag',      to: '/orderer' },
    { label: 'Nytt oppdrag', to: '/orderer/create' },
    { label: 'Fakturaer',    to: '/orderer/invoices' },
    { label: 'Prosjekter',   to: '/orderer/projects' },
    { label: 'Varsler',      to: '/orderer/varsler', badge: unreadCount },
    userDoc?.orgId
      ? { label: orgIsAdmin ? 'Team' : 'Mitt team', to: '/orderer/org' }
      : { label: 'Sett opp team', to: '/orderer/org/setup' },
  ];
  const inlineOrdererMenu = !narrow && effectiveRole === 'orderer';

  // Sjåførappen på mobil har bunnmeny og kontoinnstillinger under Profil
  if (narrow && effectiveRole === 'driver' && pathname.startsWith('/driver')) return null;
  // Bestiller: bunnmeny på rotsidene, og Nytt oppdrag har egen topp med lukkeknapp
  if (narrow && effectiveRole === 'orderer' && (isOrdererRootPath(pathname) || pathname === '/orderer/create' || pathname.startsWith('/orderer/order/') || pathname.startsWith('/orderer/org') || pathname.startsWith('/orderer/projects/'))) return null;

  return (
    <>
      <nav style={styles.nav}>
        <div
          className="tippi-nav-logo"
          style={styles.logoArea}
          onClick={() => navigate('/')}
          role="button"
          aria-label="Tippi – hjem"
        >
          <TippiLogoLight style={{ height: 26, width: 'auto' }} />
          <span style={styles.logoText}>Tippi</span>
        </div>

        {!narrow && effectiveRole === 'orderer' && (
          <div style={styles.topLinks} role="navigation" aria-label="Hovedmeny">
            {ordererLinks.map(l => {
              const on = l.to === '/orderer' ? pathname === '/orderer' : pathname.startsWith(l.to);
              return (
                <button
                  key={l.to}
                  type="button"
                  onClick={() => navTo(l.to)}
                  aria-current={on ? 'page' : undefined}
                  style={{ ...styles.topLink, ...(on ? styles.topLinkActive : {}) }}
                >
                  {l.label}
                  {l.badge > 0 && <span style={styles.topLinkBadge}>{l.badge > 9 ? '9+' : l.badge}</span>}
                </button>
              );
            })}
          </div>
        )}

        {currentUser && (
          <button
            ref={btnRef}
            style={{ ...styles.avatarBtn, background: roleColor }}
            onClick={() => setOpen(v => !v)}
            aria-label="Profil"
          >
            {initials}
          </button>
        )}
      </nav>

      {open && <div style={styles.backdrop} onClick={() => setOpen(false)} />}

      {open && (
        <div ref={panelRef} style={styles.panel}>
          {/* User card */}
          <div style={styles.userCard}>
            <div style={{ ...styles.avatarLarge, background: roleColor }}>
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {userDoc?.name && <p style={styles.userName}>{userDoc.name}</p>}
              <p style={styles.userEmail}>{currentUser?.email}</p>
              <span style={{ ...styles.roleBadge, background: roleColor }}>
                {ROLE_LABEL[effectiveRole] ?? effectiveRole}
              </span>
            </div>
          </div>

          <div style={styles.divider} />

          {/* Driver badge */}
          {effectiveRole === 'driver' && (() => {
            const badge     = computeBadge({
              completedOrders:        userDoc?.completedOrders        ?? 0,
              cancellationTimestamps: userDoc?.cancellationTimestamps ?? [],
            });
            const badgeInfo = BADGE_CONFIG[badge];
            return (
              <div style={styles.driverScore}>
                <span style={{ fontSize: 13, fontWeight: 700, color: badgeInfo.color }}>
                  {badgeInfo.icon} {badgeInfo.label}
                </span>
                <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 3 }}>
                  {userDoc?.completedOrders ?? 0} fullførte
                  {(userDoc?.cancelledOrders ?? 0) > 0 && (
                    <span style={{ color: COLORS.warning, marginLeft: 8 }}>
                      · {userDoc.cancelledOrders} kanselleringer
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Role-specific nav */}
          {effectiveRole === 'driver' && (
            <>
              <PanelItem icon="📋" label="Mine oppdrag"              onClick={() => navTo('/driver/orders')} />
              <PanelItem icon="💰" label="Inntekter og utbetalinger" onClick={() => navTo('/driver/earnings')} />
              <PanelItem icon="👤" label="Min profil"                onClick={() => navTo('/driver/profil')} />
              <PanelItem icon="🔔" label="Varsler" badge={unreadCount} onClick={() => navTo('/driver/varsler')} />
            </>
          )}
          {effectiveRole === 'orderer' && !inlineOrdererMenu && (
            <>
              <PanelItem icon="🧾" label="Fakturaer og kvitteringer" onClick={() => navTo('/orderer/invoices')} />
              <PanelItem icon="📁" label="Prosjekter"                onClick={() => navTo('/orderer/projects')} />
              <PanelItem icon="🔔" label="Varsler" badge={unreadCount} onClick={() => navTo('/orderer/varsler')} />
              {!userDoc?.orgId && (
                <PanelItem icon="🏢" label="Sett opp team" onClick={() => navTo('/orderer/org/setup')} />
              )}
              {userDoc?.orgId && orgIsAdmin && (
                <PanelItem icon="👥" label="Team" onClick={() => navTo('/orderer/org')} />
              )}
              {userDoc?.orgId && !orgIsAdmin && (
                <PanelItem icon="👥" label="Mitt team" onClick={() => navTo('/orderer/org')} />
              )}
            </>
          )}
          {effectiveRole === 'carrier' && (
            <>
              <PanelItem icon="🏢" label="Oversikt"      onClick={() => navTo('/transportor')} />
              <PanelItem icon="🚛" label="Biler"         onClick={() => navTo('/transportor/biler')} />
              <PanelItem icon="👥" label="Sjåfører"      onClick={() => navTo('/transportor/sjaforer')} />
              {carrierIsAdmin && <PanelItem icon="⚙️" label="Innstillinger" onClick={() => navTo('/transportor/innstillinger')} />}
              <PanelItem icon="🔔" label="Varsler" badge={unreadCount} onClick={() => navTo('/transportor/varsler')} />
            </>
          )}
          {effectiveRole === 'admin' && <>
            <PanelItem icon="⚖️" label="Tvister"    onClick={() => navTo('/admin/disputes')} />
            <PanelItem icon="👥" label="Brukere"    onClick={() => navTo('/admin/users')} />
            <PanelItem icon="📊" label="Inntekter"  onClick={() => navTo('/admin/revenue')} />
            <PanelItem icon="✅" label="Sjekkliste" onClick={() => navTo('/admin/checklist')} />
          </>}

          {/* Add second role / re-apply after rejection */}
          {(() => {
            const hasDriver  = userRoles.includes('driver');
            const hasOrderer = userRoles.includes('orderer');
            const driverRejected  = hasDriver  && userDoc?.driverStatus  === 'rejected';
            const ordererRejected = hasOrderer && userDoc?.ordererStatus === 'rejected';
            if (hasDriver && hasOrderer && !driverRejected && !ordererRejected) return null;
            return (
              <>
                <div style={styles.divider} />
                {!hasDriver && (
                  <PanelItem icon="➕" label="Bli sjåfør" onClick={() => navTo('/profile/add-role?role=driver')} />
                )}
                {driverRejected && (
                  <PanelItem icon="➕" label="Søk på nytt som sjåfør" onClick={() => navTo('/profile/add-role?role=driver')} />
                )}
                {!hasOrderer && (
                  <PanelItem icon="➕" label="Bli bestiller" onClick={() => navTo('/profile/add-role?role=orderer')} />
                )}
                {ordererRejected && (
                  <PanelItem icon="➕" label="Søk på nytt som bestiller" onClick={() => navTo('/profile/add-role?role=orderer')} />
                )}
              </>
            );
          })()}

          {/* Role switcher — only shows active roles */}
          {(() => {
            const driverActive  = userRoles.includes('driver')  && userDoc?.driverStatus  === 'active';
            const ordererActive = userRoles.includes('orderer') && userDoc?.ordererStatus === 'active';
            if (!driverActive || !ordererActive) {
              const driverPending  = userRoles.includes('driver')  && userDoc?.driverStatus  === 'pending';
              const ordererPending = userRoles.includes('orderer') && userDoc?.ordererStatus === 'pending';
              if (!driverPending && !ordererPending) return null;
              return (
                <>
                  <div style={styles.divider} />
                  {driverPending  && <div style={styles.pendingLabel}>Sjåfør-rolle: venter godkjenning</div>}
                  {ordererPending && <div style={styles.pendingLabel}>Bestiller-rolle: venter godkjenning</div>}
                </>
              );
            }
            return (
              <>
                <div style={styles.divider} />
                {activeRole !== 'driver' && (
                  <PanelItem icon="🚚" label="Bytt til sjåfør" onClick={() => { setActiveRole('driver'); navTo('/driver'); }} />
                )}
                {activeRole !== 'orderer' && (
                  <PanelItem icon="📦" label="Bytt til bestiller" onClick={() => { setActiveRole('orderer'); navTo('/orderer'); }} />
                )}
              </>
            );
          })()}

          {/* Transportør: bytt til og fra transportørrollen */}
          {(() => {
            if (!(userRoles.includes('carrier') && userDoc?.carrierOrgId && userRoles.length > 1)) return null;
            const driverActive  = userRoles.includes('driver')  && userDoc?.driverStatus  === 'active';
            const ordererActive = userRoles.includes('orderer') && userDoc?.ordererStatus === 'active';
            // Er både sjåfør og bestiller aktive, tilbyr bryteren over allerede begge byttene
            const both = driverActive && ordererActive;
            return (
              <>
                <div style={styles.divider} />
                {activeRole !== 'carrier' && (
                  <PanelItem icon="🏢" label="Bytt til transportør" onClick={() => { setActiveRole('carrier'); navTo('/transportor'); }} />
                )}
                {activeRole === 'carrier' && !both && ordererActive && (
                  <PanelItem icon="📦" label="Bytt til bestiller" onClick={() => { setActiveRole('orderer'); navTo('/orderer'); }} />
                )}
                {activeRole === 'carrier' && !both && driverActive && (
                  <PanelItem icon="🚚" label="Bytt til sjåfør" onClick={() => { setActiveRole('driver'); navTo('/driver'); }} />
                )}
              </>
            );
          })()}

          {/* Admin: vis som */}
          {userRoles.includes('admin') && (
            <>
              <div style={styles.divider} />
              <div style={styles.sectionLabel}>Vis som</div>
              <PanelItem
                icon={viewingAs === 'driver' ? '✓' : ' '}
                label="Sjåfør"
                onClick={() => { setViewingAs('driver'); navTo('/driver'); setOpen(false); }}
              />
              <PanelItem
                icon={viewingAs === 'orderer' ? '✓' : ' '}
                label="Bestiller"
                onClick={() => { setViewingAs('orderer'); navTo('/orderer'); setOpen(false); }}
              />
              <PanelItem
                icon={!viewingAs || viewingAs === 'admin' ? '✓' : ' '}
                label="Admin"
                onClick={() => { setViewingAs('admin'); navTo('/admin'); setOpen(false); }}
              />
            </>
          )}

          <ThemeSwitcher />

          <PanelItem icon="🔒" label="Personvern" onClick={() => navTo('/personvern')} />

          <div style={styles.divider} />

          <button style={styles.logoutBtn} onClick={handleLogout}>
            Logg ut
          </button>
        </div>
      )}
    </>
  );
}

export function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const options = [
    { value: 'system', label: 'System' },
    { value: 'light',  label: 'Lys' },
    { value: 'dark',   label: 'Mørk' },
  ];
  return (
    <div style={styles.themeSwitcher}>
      <span style={styles.themeSwitcherLabel}>Utseende</span>
      <div style={styles.themeSwitcherRow}>
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            style={{
              ...styles.themeOption,
              ...(theme === opt.value ? styles.themeOptionActive : {}),
            }}
            onClick={() => setTheme(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function PanelItem({ icon, label, onClick, badge }) {
  return (
    <button className="tippi-panel-item" style={styles.panelItem} onClick={onClick}>
      <span style={{ fontSize: 17, width: 24, textAlign: 'center' }}>{icon}</span>
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
      {badge > 0 && (
        <span style={{
          background: '#e53e3e',
          color: '#fff',
          borderRadius: 10,
          padding: '1px 6px',
          fontSize: 11,
          fontWeight: 700,
          minWidth: 18,
          textAlign: 'center',
          marginRight: 4,
        }}>
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      <span style={{ color: '#ccc', fontSize: 16 }}>›</span>
    </button>
  );
}

const styles = {
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 18px', height: 56, background: COLORS.brandPanel,
    flexShrink: 0, zIndex: 200, position: 'relative',
  },
  logoArea: {
    display: 'flex', alignItems: 'center', gap: 9,
  },
  logoText: {
    fontFamily: FONTS.display,
    fontWeight: 700, fontSize: 22,
    letterSpacing: 1, color: '#fff',
  },
  topLinks: {
    display: 'flex', alignItems: 'center', gap: 4, flex: 1, justifyContent: 'center', minWidth: 0,
  },
  topLink: {
    position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
    color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
    padding: '8px 12px', borderRadius: 8, whiteSpace: 'nowrap',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  topLinkActive: { color: '#fff', background: 'rgba(255,255,255,0.14)' },
  topLinkBadge: {
    background: '#e53e3e', color: '#fff', borderRadius: 10, padding: '1px 6px',
    fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'center',
  },
  avatarBtn: {
    width: 36, height: 36, borderRadius: '50%', border: 'none',
    color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, transition: 'opacity 0.15s',
  },
  backdrop: {
    position: 'fixed', top: 56, left: 0, right: 0, bottom: 0, zIndex: 299,
  },
  panel: {
    position: 'fixed', top: 56, right: 0,
    width: 'min(320px, 100vw)',
    background: COLORS.white,
    borderRadius: '0 0 0 14px',
    boxShadow: SHADOWS.lg,
    zIndex: 300,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  userCard: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '20px 20px 16px',
  },
  avatarLarge: {
    width: 48, height: 48, borderRadius: '50%',
    color: '#fff', fontWeight: 700, fontSize: 18,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  userName: {
    fontWeight: 700, fontSize: 15, color: COLORS.textDark,
    margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  userEmail: {
    fontSize: 12, color: COLORS.textLight, margin: '2px 0 6px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  roleBadge: {
    display: 'inline-block', borderRadius: 4, padding: '2px 8px',
    fontSize: 11, fontWeight: 700, color: '#fff',
  },
  driverScore: {
    padding: '11px 20px',
    background: COLORS.surfaceHover,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  divider: {
    height: 1, background: COLORS.border, margin: '0 16px',
  },
  panelItem: {
    display: 'flex', alignItems: 'center', gap: 12,
    width: '100%', padding: '13px 20px',
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 14, color: COLORS.textDark,
  },
  logoutBtn: {
    margin: 16, padding: '12px',
    borderRadius: 8, border: 'none',
    background: COLORS.dangerBg, color: COLORS.dangerText,
    fontWeight: 700, fontSize: 14, cursor: 'pointer',
    transition: 'background 0.15s',
  },
  sectionLabel: {
    padding: '6px 20px 2px',
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  pendingLabel: {
    padding: '8px 20px',
    fontSize: 13,
    color: COLORS.warning,
    fontStyle: 'italic',
  },
  themeSwitcher: {
    padding: '10px 20px 4px',
  },
  themeSwitcherLabel: {
    fontSize: 11, fontWeight: 700, color: COLORS.textLight,
    textTransform: 'uppercase', letterSpacing: 0.8,
  },
  themeSwitcherRow: {
    display: 'flex', gap: 6, marginTop: 8,
  },
  themeOption: {
    flex: 1, padding: '7px 0', borderRadius: 6,
    border: `1px solid ${COLORS.border}`, background: 'none',
    color: COLORS.textMid, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  themeOptionActive: {
    background: COLORS.primary, borderColor: COLORS.primary, color: COLORS.onPrimary,
  },
};
